import { EV } from '../core/eventbus.js';
import { Tags } from '../systems/tags.js';
import { Inspire } from '../systems/inspire.js';
import { FRAGMENTS, TRACKS } from '../data/inspire.js';
import { CERT_DEFS } from '../data/tags.js';

/**
 * 「你忽然明白了什么」——灵感那一层的亮相。
 *
 * 三种时刻各有一副说法：
 *   碎片   心里冒出一句话（只给意象，不给缘由）
 *   资格证 两块碎片撞上了，忽然明白一件事，得一张照
 *   境界   灵气攒够了，坐到了另一个地方
 *
 * 不挡手、不占屏（pointer-events: none），说完自己淡掉。
 */

const CSS = `
#insight {
  position: fixed; inset: 0; display: flex; align-items: center; justify-content: center;
  pointer-events: none; z-index: 90; opacity: 0; transition: opacity .45s ease;
}
#insight.on { opacity: 1; }
#insight .insight-box {
  text-align: center; padding: 26px 34px; border-radius: 4px;
  background: rgba(255,255,255,.86); box-shadow: 0 0 0 1px rgba(0,0,0,.08), 0 10px 40px rgba(0,0,0,.14);
  max-width: 76vw;
}
#insight .insight-lead { font-size: 14px; letter-spacing: .3em; color: #8a7a5c; }
#insight .insight-name { font-size: 30px; margin: 10px 0 8px; color: #2b2b2b; letter-spacing: .1em; }
#insight .insight-text { font-size: 16px; color: #5a5a5a; line-height: 1.9; }
#insight .insight-perk { margin-top: 10px; font-size: 13px; color: #8a7a5c; }
#insight.flash::after {
  content: ''; position: absolute; inset: 0; background: #fff; opacity: 0;
  animation: insight-flash 1.1s ease-out 1;
}
@keyframes insight-flash { 0% { opacity: .72; } 100% { opacity: 0; } }
`;

function ensureStyle() {
  if (document.getElementById('insight-style')) return;
  const st = document.createElement('style');
  st.id = 'insight-style';
  st.textContent = CSS;
  document.head.appendChild(st);
}

export class InsightPanel {
  constructor(engine) {
    this.engine = engine;
    ensureStyle();
    let el = document.getElementById('insight');
    if (!el) {
      el = document.createElement('div');
      el.id = 'insight';
      document.body.appendChild(el);
    }
    this.el = el;
    this._timer = 0;
    engine.events.on(EV.INSIGHT, (p) => this.show(p));
  }

  show(p = {}) {
    const kind = p.kind || 'fragment';
    const lead = kind === 'cert' ? '你忽然明白了什么'
      : kind === 'realm' ? '你坐到了另一个地方'
        : '你心里冒出一句话';
    const title = kind === 'cert' ? `你获得了：${p.name || ''}` : (p.name || '');
    const box = document.createElement('div');
    box.className = 'insight-box';
    box.innerHTML = `
      <div class="insight-lead">${lead}</div>
      <div class="insight-name">${title}</div>
      <div class="insight-text">${p.text || ''}</div>
      ${p.perk ? `<div class="insight-perk">${p.perk}</div>` : ''}
    `;
    this.el.innerHTML = '';
    this.el.appendChild(box);
    // 重播一次闪白：先摘掉 class 再下一帧加上，动画才会重新跑
    this.el.classList.remove('flash');
    void this.el.offsetWidth;
    this.el.classList.add('flash', 'on');
    clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      this.el.classList.remove('on');
    }, kind === 'fragment' ? 2600 : 3600);
  }
}

/**
 * 行囊最上面那几行：境界、身上的照、心里攒着的念头。
 * 只给名字，不给数——修行到哪一步、差多少点，一律不说。
 */
export function personNote(db) {
  const out = [];
  const realm = Inspire.realm(db);
  if (realm.level > 0) {
    out.push(`打坐的功夫：${realm.name}${realm.next ? `（再坐坐，兴许能到${realm.next}）` : ''}`);
  }
  // 手艺照只报每门最高那一档（「火房帮工、灶上伙计」并排写太啰嗦），
  // 衙署发的那几张（行商牌、学子牌……）照常一张一张列
  const trackCerts = new Set();
  const shown = [];
  for (const t of Object.values(TRACKS)) {
    for (let i = t.levels.length - 1; i >= 0; i -= 1) {
      trackCerts.add(t.levels[i].cert);
      if (Tags.has(db, t.levels[i].cert)) { shown.push(CERT_DEFS[t.levels[i].cert].name); break; }
    }
  }
  const adminCerts = Tags.certs(db)
    .filter((c) => !trackCerts.has(c.id))
    .map((c) => c.name);
  const certs = [...adminCerts, ...shown];
  if (certs.length) out.push(`身上的照：${certs.join('、')}`);
  const frags = Inspire.ownedFrags(db)
    .map((k) => (FRAGMENTS[k] ? FRAGMENTS[k].name : null))
    .filter(Boolean);
  if (frags.length) out.push(`心里攒着：${frags.join('、')}`);
  // 修行到「感气」以上，才知道怎么回头看自己
  if (realm.level >= 1) out.push('（摆一面照心镜，坐下来能看清自己攒了多少）');
  return out;
}
